import { BodyRegular } from '@components/atoms/typography';
import PlayerImage from '@components/player/image';
import { CURRENT_SEASON, MAX_GAMEWEEK } from '@lib/constants';
import { fetchPlayers } from '@api-service/players';
import type { PlayerStatType } from '@components/comparison/stats-table';
import type { Player as FfhPlayer } from '@api-service/players';
import type { PlayerStatsRow } from '@api-service/player-stats';

export const POSITION_ID_BY_LABEL: Record<FfhPlayer['position'], number> = {
  GK: 1,
  DEF: 2,
  MID: 3,
  FWD: 4
};

/**
 * Minimal legacy player shape consumed by SuggestedTransferChart via
 * playerName and playerGameweekPrediction in @lib/player-functions.
 */
export type PredictionChartPlayer = {
  fpl: { web_name: string };
  data: { predictions: Array<{ gw: number; predicted_pts: number }> };
};

export const toPredictionChartPlayer = (
  player: FfhPlayer
): PredictionChartPlayer => {
  const pointsByGameweek = new Map<number, number>();
  for (const fixture of player.fixtures) {
    pointsByGameweek.set(
      fixture.gameweek,
      (pointsByGameweek.get(fixture.gameweek) ?? 0) + fixture.predictions.points
    );
  }
  return {
    fpl: { web_name: player.displayName },
    data: {
      predictions: [...pointsByGameweek.entries()]
        .sort(([gameweekA], [gameweekB]) => gameweekA - gameweekB)
        .map(([gw, points]) => ({ gw, predicted_pts: points }))
    }
  };
};

/**
 * Player ids are re-minted each season, so a stats lookup for a past season
 * silently returns no rows when given current-season ids. This resolves the
 * requested season's id for each player via the stable optaId. Players with
 * no record in that season (e.g. new signings) are simply absent from the
 * returned map.
 */
export const resolveSeasonPlayerIds = async (
  optaIds: string[],
  season: number,
  accessToken?: string
): Promise<Map<string, string>> => {
  if (optaIds.length === 0) return new Map();
  const response = await fetchPlayers({
    optaIds: optaIds.join(','),
    season,
    exclude: 'predictions',
    limit: optaIds.length,
    ...(accessToken ? { accessToken } : {})
  });
  return new Map(response.data.map((player) => [player.optaId, player.id]));
};

export type UpcomingGameweek = {
  gameweek: number;
  fixtures: Array<{ opponentShortName: string; isHome: boolean }>;
  predictedPoints: number;
};

/**
 * Groups a player's fixtures into one entry per gameweek across a fixed
 * window, so double gameweeks aggregate and blank gameweeks still render.
 */
export const buildUpcomingGameweeks = (
  player: FfhPlayer,
  firstGameweek: number,
  gameweekCount: number
): UpcomingGameweek[] => {
  const byGameweek = new Map<number, UpcomingGameweek>();
  for (const fixture of player.fixtures) {
    const existing = byGameweek.get(fixture.gameweek) ?? {
      gameweek: fixture.gameweek,
      fixtures: [],
      predictedPoints: 0
    };
    existing.fixtures.push({
      opponentShortName: fixture.opponent.shortName,
      isHome: fixture.isHome
    });
    existing.predictedPoints += fixture.predictions.points;
    byGameweek.set(fixture.gameweek, existing);
  }

  const lastGameweek = Math.min(
    firstGameweek + gameweekCount - 1,
    MAX_GAMEWEEK
  );
  const gameweeks: UpcomingGameweek[] = [];
  for (let gameweek = firstGameweek; gameweek <= lastGameweek; gameweek += 1) {
    gameweeks.push(
      byGameweek.get(gameweek) ?? {
        gameweek,
        fixtures: [],
        predictedPoints: 0
      }
    );
  }
  return gameweeks;
};

export type GametimeOption = {
  value: 'perstart' | 'perapp' | 'per90';
  label: string;
};

export type ComparisonFilters = {
  minGW: number;
  maxGW: number;
  venue: 'all' | 'home' | 'away';
  season: number;
};

export type ComparisonState = {
  playersA: FfhPlayer[];
  playersB: FfhPlayer[];
  playerStatsA: PlayerStatType[];
  playerStatsB: PlayerStatType[];
  /**
   * The raw season-total rows behind each side, kept alongside the derived
   * table stats because the spider chart needs the underlying numbers to
   * normalise, not the already-percentaged table values.
   */
  statsRowsA: PlayerStatsRow[];
  statsRowsB: PlayerStatsRow[];
  gametimeType: GametimeOption;
  filters: ComparisonFilters;
};

export const GAMETIME_OPTIONS: GametimeOption[] = [
  { value: 'perstart', label: 'Per Start' },
  { value: 'perapp', label: 'Per Appearance' },
  { value: 'per90', label: 'Per 90 minutes' }
];

export const initialState: ComparisonState = {
  playersA: [],
  playersB: [],
  playerStatsA: [],
  playerStatsB: [],
  statsRowsA: [],
  statsRowsB: [],
  gametimeType: GAMETIME_OPTIONS[0],
  filters: {
    minGW: 1,
    maxGW: 38,
    venue: 'all',
    season: CURRENT_SEASON
  }
};

export const togglePlayerOnSide = (
  state: ComparisonState,
  side: 'A' | 'B',
  player: FfhPlayer
): ComparisonState => {
  const playersKey = side === 'A' ? 'playersA' : 'playersB';
  const isSelected = state[playersKey].some(
    (existing) => existing.optaId === player.optaId
  );

  return {
    ...state,
    [playersKey]: isSelected
      ? state[playersKey].filter(
          (existing) => existing.optaId !== player.optaId
        )
      : [...state[playersKey], player]
  };
};

export const PlayerImageChip = ({ player }: { player: FfhPlayer }) => (
  <div className="flex items-center">
    <div className="relative flex items-center justify-center w-8 h-8 mr-3 text-xs rounded-full">
      <PlayerImage
        src={player.imageUrl}
        kitImageUrl={player.kitImageUrl}
        height={32}
        width={32}
        fallbackHeight={24}
        fallbackWidth={24}
      />
    </div>
    <div>
      <BodyRegular>{player.displayName}</BodyRegular>
    </div>
  </div>
);
